import { createContext, ReactNode, useContext, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useScrollRestoration } from 'use-scroll-restoration';
import { useResizeObserver } from 'usehooks-ts';

import { useBreadcrumbs } from 'services/breadcrumbs/breadcrumbs';
import { EMPTY_FUNCTION } from 'support/helpers/const/const';
import { classNames } from 'support/helpers/generic/generic';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { LoadingPage } from 'components/Loading/LoadingPage';

import { PageContextType, StickyMode, usePageContext } from './contexts/PageContext';
import { usePartialStickyHeader } from './hooks/usePartialStickyHeader';
import {
  BASE_CLASSES,
  getBorderClasses,
  getPaddingClasses,
  STICKY_CLASSES,
  STICKY_WITH_TRANSITION,
  TRANSITION_CLASSES,
} from './helpers/styleHelpers';
import { NavigationSidebar } from './NavigationSidebar/NavigationSidebar';

export const PAGE_SECTION_PORTAL_ROOT_ID = 'page-section-root';

const PageContext = createContext<PageContextType>({
  stickyMode: 'partial',
  needsFixedHeight: false,
  registerSection: EMPTY_FUNCTION,
  unregisterSection: EMPTY_FUNCTION,
});

type PageProps = {
  children: ReactNode;
  isLoading?: boolean;
  grayBackground?: boolean;
  sidebar?: ReactNode;
  isInternal?: boolean;
  isScrollable?: boolean;
  stickyMode?: StickyMode;
};

function PageWrapper({
  children,
  isLoading,
  grayBackground,
  sidebar,
  isInternal = false,
  isScrollable = true,
  stickyMode,
}: PageProps) {
  /*
   * We need the useScrollRestoration here since the ScrollRestoration from react-router does not allow to select the container
   * Our scrollable container is not the window but the div with the class overflow-scroll
   */
  const key = `scroll-position-${useLocation().key}`;
  const { ref } = useScrollRestoration(key, {
    debounceTime: 200,
    persist: 'localStorage',
  });

  const contextValue = usePageContext(stickyMode);

  return (
    <PageContext.Provider value={contextValue}>
      <div className="h-screen w-full">
        <div className={classNames('w-full h-full flex overflow-hidden', isInternal ? 'pt-4' : undefined)}>
          <div className="sticky top-0 z-10 flex h-full">{(sidebar ?? isInternal) ? null : <NavigationSidebar />}</div>
          <div
            ref={ref}
            className={classNames(
              isScrollable ? 'overflow-y-auto' : undefined,
              'h-full w-full overflow-x-auto',
              grayBackground ? 'bg-gray-100' : 'bg-white',
            )}
          >
            {isLoading ? (
              <div className="mx-auto h-screen">
                <LoadingPage />
              </div>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </PageContext.Provider>
  );
}

const Title = ({ children }: { children: ReactNode }) => (
  <h3 className="flex items-center gap-2 text-lg font-bold leading-10 text-gray-900">{children}</h3>
);
const Description = ({ children }: { children: ReactNode }) => (
  <p className="text-sm font-normal text-gray-500">{children}</p>
);

const Head = ({
  children,
  rightContent,
  title,
  description,
  padding,
  border,
}: {
  rightContent?: ReactNode;
  children?: ReactNode;
  title?: ReactNode;
  description?: string;
  padding?: {
    top?: boolean;
    bottom?: boolean;
    left?: boolean;
    right?: boolean;
  };
  border?: {
    bottom?: boolean;
  };
}) => {
  const { stickyMode } = useContext(PageContext);
  const breadcrumbs = useBreadcrumbs();
  const paddingConfig = {
    top: padding?.top ?? true,
    bottom: padding?.bottom ?? true,
    left: padding?.left ?? true,
    right: padding?.right ?? true,
  };

  const borderConfig = {
    bottom: border?.bottom ?? true,
  };

  const headerRef = useRef<HTMLDivElement>(null);
  const { height = 0 } = useResizeObserver({
    ref: headerRef,
    box: 'border-box',
  });

  useLayoutEffect(() => {
    if (stickyMode === 'full') {
      document.documentElement.style.setProperty('--header-height', `${height}px`);

      return () => {
        document.documentElement.style.setProperty('--header-height', `0px`);
      };
    }
  }, [height, stickyMode]);

  const content = (
    <div className="relative flex min-h-[38px] w-full shrink-0 justify-between gap-2">
      {breadcrumbs.length > 1 ? (
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      ) : title ? (
        <div className="w-full">
          <Title>{title}</Title>
          {description ? <Description>{description}</Description> : null}
        </div>
      ) : null}
      <div className="ml-auto flex items-center gap-2">{rightContent}</div>
    </div>
  );

  const { scrollableSectionRef, scrollableSectionHeight, isHeaderVisible } = usePartialStickyHeader(
    stickyMode === 'partial',
  );

  if (stickyMode === 'partial') {
    return (
      <div ref={headerRef} className="contents">
        <div
          ref={scrollableSectionRef}
          className={classNames(
            BASE_CLASSES,
            'sticky top-0 z-20',
            TRANSITION_CLASSES,
            !isHeaderVisible && children && '-translate-y-full',
            getBorderClasses(borderConfig, true, Boolean(children)),
            getPaddingClasses(paddingConfig, { skipBottom: Boolean(children) }),
          )}
        >
          <div className="flex">{content}</div>
        </div>

        {children ? (
          <div
            className={classNames(
              BASE_CLASSES,
              STICKY_WITH_TRANSITION,
              getBorderClasses(borderConfig),
              getPaddingClasses(paddingConfig, { reducedTop: true }),
            )}
            style={{ top: isHeaderVisible ? `${scrollableSectionHeight}px` : '0px' }}
          >
            <div className="flex w-full justify-between gap-2">{children}</div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      ref={headerRef}
      className={classNames(
        BASE_CLASSES,
        STICKY_CLASSES,
        getBorderClasses(borderConfig),
        getPaddingClasses(paddingConfig),
      )}
    >
      <div className="flex">{content}</div>
      {children ? <div className="mt-2 flex w-full justify-between gap-2">{children}</div> : null}
    </div>
  );
};

const Section = ({
  children,
  leftAside,
  rightAside,
  topBanner,
  bottomBanner,
  testId,
  isLoading,
}: {
  children: ReactNode;
  leftAside?: ReactNode;
  rightAside?: ReactNode;
  topBanner?: ReactNode;
  bottomBanner?: ReactNode;
  testId?: string;
  isLoading?: boolean;
}) => {
  const { needsFixedHeight, registerSection, unregisterSection } = useContext(PageContext);

  useLayoutEffect(() => {
    registerSection({
      hasLeftAside: Boolean(leftAside),
      hasRightAside: Boolean(rightAside),
      hasBottomBanner: Boolean(bottomBanner),
    });

    return () => {
      unregisterSection();
    };
  }, [leftAside, rightAside, bottomBanner, registerSection, unregisterSection]);

  return (
    <section className="relative overflow-x-auto" data-testid={testId} id={PAGE_SECTION_PORTAL_ROOT_ID}>
      <div className="flex w-full">
        {leftAside ? <div className="h-full">{leftAside}</div> : null}
        <div
          className={classNames(
            'relative flex w-full flex-col min-w-0',
            needsFixedHeight ? 'overflow-y-auto' : undefined,
          )}
          style={needsFixedHeight ? { height: `var(--content-height)` } : undefined}
        >
          {topBanner ? <div className="sticky inset-x-0 top-0 z-20">{topBanner}</div> : null}
          {isLoading ? <LoadingPage /> : <div className="flex-1 px-4 py-6 sm:px-6 md:px-8">{children}</div>}
          {bottomBanner ? <div className="sticky inset-x-0 bottom-0 z-20">{bottomBanner}</div> : null}
        </div>
        {rightAside ? <div className="h-full">{rightAside}</div> : null}
      </div>
    </section>
  );
};

export const Page = Object.assign(PageWrapper, { Head, Section, Title, Description });
